import React, { type FC, useEffect, useState } from 'react';
import { useCoordination } from '@atlassiansox/engagekit-ts';
import { injectIntl } from 'react-intl-next';

import { SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import {
	VERIFIED_CONTENT_PULSE_KEY,
	EventSourceName,
} from '@confluence/automation-discovery/entry-points/constants';
import { useAutomationPermissionService } from '@confluence/automation-menu/entry-points/AutomationPermissionService';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useCoordinationClient } from '@confluence/engagement-provider';
import { VerifiedContentSpotlight } from '@confluence/automation-discovery/entry-points/VerifiedContentAutomationSpotlight';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey';

import type { RendererContentStateComponentProps } from './types';
import { RendererContentStateComponent } from './RendererContentStateComponent';
import { StyledBorder } from './StyledBorder';

const setUserHasSeenVerifiedContentSpotlight = (): void => {
	localStorage.setItem(keys.AUTOMATION_DISCOVER_VERIFIED_CONTENT, true);
};

const hasUserSeenVerifiedContentSpotlight = (): boolean => {
	return localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_VERIFIED_CONTENT);
};

const RendererContentStateWithSpotlightImpl: FC<RendererContentStateComponentProps> = ({
	contentId,
	contentState,
	lastUpdatedInfo,
	isDraft,
	hasSeparator = true,
}) => {
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const coordinationClient = useCoordinationClient();
	const [pulseStarted, stopPulse] = useCoordination(coordinationClient, VERIFIED_CONTENT_PULSE_KEY);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const spaceKey = useSpaceKey();
	const { edition } = useSessionData();

	const isPremium = ConfluenceEdition.PREMIUM === edition;
	const { canManageAutomations } = useAutomationPermissionService(spaceKey || '');

	const handleProceed = () => {
		setIsSpotlightActive(false);
		if (pulseStarted) {
			void stopPulse();
		}
		window.open(
			`/wiki/spaces/${spaceKey}/settings/automation#/rule/template?templateId=confluence_notify_template_7`,
			'_blank',
		);
		setUserHasSeenVerifiedContentSpotlight();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'automationDiscoveryVerifiedContentProceed',
				source: EventSourceName.VERIFED_CONTENT,
				attributes: {
					spaceKey,
				},
			},
		}).fire();
	};

	const handleDismiss = () => {
		setIsSpotlightActive(false);
		if (pulseStarted) {
			void stopPulse();
		}
		setUserHasSeenVerifiedContentSpotlight();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'automationDiscoveryVerifiedContentDismiss',
				source: EventSourceName.VERIFED_CONTENT,
				attributes: {
					spaceKey,
				},
			},
		}).fire();
	};

	const showSpotlight = () => {
		// Spotlight is disabled
		setIsSpotlightActive(false);
	};

	const isVerifiedContentState =
		contentState?.restrictionLevel === 'PAGE_OWNER' && contentState.name === 'Verified';

	useEffect(() => {
		if (
			isPremium &&
			isVerifiedContentState &&
			!hasUserSeenVerifiedContentSpotlight() &&
			canManageAutomations
		) {
			setTimeout(() => {
				showSpotlight();
			}, 1);
		}
	}, [isPremium, isVerifiedContentState, canManageAutomations]);

	return (
		<>
			<SpotlightTarget name="verified-content-state">
				<RendererContentStateComponent
					contentId={contentId}
					contentState={contentState}
					lastUpdatedInfo={lastUpdatedInfo}
					isDraft={isDraft}
					hasSeparator={hasSeparator}
				/>
			</SpotlightTarget>
			<SpotlightTransition>
				{
					/* TODO A4C-3826: Remove this spotlight */
					isSpotlightActive && (
						<VerifiedContentSpotlight handleDismiss={handleDismiss} handleProceed={handleProceed} />
					)
				}
			</SpotlightTransition>
			{/* Move the following border back to RendererContentStateComponent when removing spotlight */}
			{!isDraft && hasSeparator ? <StyledBorder /> : null}
		</>
	);
};

export const RendererContentStateWithSpotlight = injectIntl(RendererContentStateWithSpotlightImpl);
